/**
 * Contratos das entidades do CRM 360 (`crm_opportunities`/`crm_opportunity_events`/
 * `crm_quotes`/`crm_quote_items`/`crm_tasks`/`crm_segments`, migration 0032, e
 * o cliente canônico de `customers`/`customer_identities`, migration 0020).
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): estes tipos e as
 * funções `assertCrm*` foram promovidos de `apps/plataforma/src/api/types.ts`
 * para `packages/crm`, para a Edge Function `supabase/functions/crm-runtime`
 * e o app `apps/plataforma` lerem o MESMO contrato sem a borda depender do app
 * (ADR-0004, handoffs H-12.A/H-13.A). `api/types.ts` passou a reexportar daqui.
 *
 * O que NÃO aparece aqui, de propósito: um "cliente do CRM". O cadastro é o
 * cliente CANÔNICO (`customers` + `customer_identities`, 0020) e a empresa é
 * `companies`/`company_members` (0018); `CrmCustomer`/`CrmCompany` são a
 * LEITURA desses registros, não um cadastro paralelo.
 */
import { isLeadStage, isOpportunitySource, isQuoteStatus, isTaskKind, isTaskStatus } from "./pipeline.js";
function fail(entity, reason) {
    throw new Error(`Invalid ${entity}: ${reason}`);
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
export function assertCrmCustomer(value) {
    if (!isRecord(value))
        fail("CrmCustomer", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("CrmCustomer", "missing id");
    if (typeof value.name !== "string" || !value.name)
        fail("CrmCustomer", "missing name");
    if (value.phone !== null && typeof value.phone !== "string")
        fail("CrmCustomer", "phone must be a string or null");
    if (value.email !== null && typeof value.email !== "string")
        fail("CrmCustomer", "email must be a string or null");
    if (value.companyId !== null && typeof value.companyId !== "string") {
        fail("CrmCustomer", "companyId must be a string or null");
    }
    if (!Array.isArray(value.identities))
        fail("CrmCustomer", "identities must be an array");
    for (const identity of value.identities) {
        if (!isRecord(identity))
            fail("CrmCustomerIdentity", "identity is not an object");
        const kind = identity.kind;
        if (kind !== "whatsapp_phone" && kind !== "email" && kind !== "marketplace_id" && kind !== "cpf_cnpj") {
            fail("CrmCustomerIdentity", `unknown kind ${String(kind)}`);
        }
        if (typeof identity.value !== "string" || !identity.value)
            fail("CrmCustomerIdentity", "missing value");
        if (typeof identity.verified !== "boolean")
            fail("CrmCustomerIdentity", "missing verified");
    }
    return value;
}
export function assertCrmCompany(value) {
    if (!isRecord(value))
        fail("CrmCompany", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("CrmCompany", "missing id");
    if (typeof value.legalName !== "string" || !value.legalName)
        fail("CrmCompany", "missing legalName");
    const status = value.status;
    if (status !== "pending" && status !== "approved" && status !== "rejected" && status !== "suspended") {
        fail("CrmCompany", `unknown status ${String(status)}`);
    }
    if (!Array.isArray(value.contacts))
        fail("CrmCompany", "contacts must be an array");
    for (const contact of value.contacts) {
        if (!isRecord(contact) || typeof contact.profileId !== "string" || !contact.profileId) {
            fail("CrmCompanyContact", "missing profileId");
        }
        if (contact.companyRole !== "owner" && contact.companyRole !== "member") {
            fail("CrmCompanyContact", `unknown companyRole ${String(contact.companyRole)}`);
        }
    }
    return value;
}
export function assertCrmOpportunity(value) {
    if (!isRecord(value))
        fail("CrmOpportunity", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("CrmOpportunity", "missing id");
    if (typeof value.customerId !== "string" || !value.customerId)
        fail("CrmOpportunity", "missing customerId");
    if (typeof value.title !== "string" || !value.title)
        fail("CrmOpportunity", "missing title");
    if (!isLeadStage(value.stage))
        fail("CrmOpportunity", `unknown stage ${String(value.stage)}`);
    if (!isOpportunitySource(value.source))
        fail("CrmOpportunity", `unknown source ${String(value.source)}`);
    // Responsável é obrigatório (coluna `not null` da 0032): oportunidade sem
    // dono não é acompanhada por ninguém.
    if (typeof value.ownerProfileId !== "string" || !value.ownerProfileId)
        fail("CrmOpportunity", "missing ownerProfileId");
    if (typeof value.ownerLabel !== "string" || !value.ownerLabel)
        fail("CrmOpportunity", "missing ownerLabel");
    if (value.valueCents !== null && typeof value.valueCents !== "number") {
        fail("CrmOpportunity", "valueCents must be a number or null");
    }
    // Prova do fechamento: `perdido` sem motivo e `ganho` sem o pedido que a
    // concretizou inflariam o funil com resultados que nada sustenta.
    if (value.stage === "perdido" && (typeof value.lostReason !== "string" || !value.lostReason)) {
        fail("CrmOpportunity", "stage perdido requires lostReason");
    }
    if (value.stage === "ganho" && (typeof value.platformOrderId !== "string" || !value.platformOrderId)) {
        fail("CrmOpportunity", "stage ganho requires platformOrderId");
    }
    if (!Array.isArray(value.events))
        fail("CrmOpportunity", "events must be an array");
    for (const event of value.events) {
        if (!isRecord(event) || typeof event.id !== "string" || !event.id)
            fail("CrmOpportunityEvent", "missing id");
        if (!isLeadStage(event.toStage))
            fail("CrmOpportunityEvent", `unknown toStage ${String(event.toStage)}`);
        if (event.fromStage !== null && !isLeadStage(event.fromStage)) {
            fail("CrmOpportunityEvent", `unknown fromStage ${String(event.fromStage)}`);
        }
        // Mesma regra da linha do tempo do pedido (0031): transição sem autor não
        // sustenta auditoria.
        if (typeof event.actor !== "string" || !event.actor)
            fail("CrmOpportunityEvent", "missing actor");
        if (event.actorKind !== "staff" && event.actorKind !== "system") {
            fail("CrmOpportunityEvent", `unknown actorKind ${String(event.actorKind)}`);
        }
        if (typeof event.occurredAt !== "string")
            fail("CrmOpportunityEvent", "missing occurredAt");
    }
    return value;
}
export function assertCrmQuote(value) {
    if (!isRecord(value))
        fail("CrmQuote", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("CrmQuote", "missing id");
    if (typeof value.number !== "string" || !value.number)
        fail("CrmQuote", "missing number");
    if (typeof value.customerId !== "string" || !value.customerId)
        fail("CrmQuote", "missing customerId");
    if (!isQuoteStatus(value.status))
        fail("CrmQuote", `unknown status ${String(value.status)}`);
    if (typeof value.ownerProfileId !== "string" || !value.ownerProfileId)
        fail("CrmQuote", "missing ownerProfileId");
    if (typeof value.subtotalCents !== "number")
        fail("CrmQuote", "missing subtotalCents");
    if (typeof value.discountCents !== "number")
        fail("CrmQuote", "missing discountCents");
    if (value.shippingCents !== null && typeof value.shippingCents !== "number") {
        fail("CrmQuote", "shippingCents must be a number or null");
    }
    if (value.totalCents !== null && typeof value.totalCents !== "number") {
        fail("CrmQuote", "totalCents must be a number or null");
    }
    if (!Array.isArray(value.items))
        fail("CrmQuote", "items must be an array");
    if (!value.items.length)
        fail("CrmQuote", "a quote without items is not a proposal");
    for (const item of value.items) {
        if (!isRecord(item) || typeof item.sku !== "string" || !item.sku)
            fail("CrmQuoteItem", "missing sku");
        if (!Number.isInteger(item.quantity) || item.quantity <= 0) {
            fail("CrmQuoteItem", "quantity must be a positive integer");
        }
        if (typeof item.unitPriceCents !== "number")
            fail("CrmQuoteItem", "missing unitPriceCents");
        if (item.unitCostCents !== null && typeof item.unitCostCents !== "number") {
            fail("CrmQuoteItem", "unitCostCents must be a number or null");
        }
    }
    return value;
}
export function assertCrmTask(value) {
    if (!isRecord(value))
        fail("CrmTask", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("CrmTask", "missing id");
    if (typeof value.customerId !== "string" || !value.customerId)
        fail("CrmTask", "missing customerId");
    if (typeof value.title !== "string" || !value.title)
        fail("CrmTask", "missing title");
    if (!isTaskKind(value.kind))
        fail("CrmTask", `unknown kind ${String(value.kind)}`);
    if (!isTaskStatus(value.status))
        fail("CrmTask", `unknown status ${String(value.status)}`);
    // Prazo e dono são `not null` na 0032: tarefa sem os dois é lembrete que
    // ninguém cumpre e não pode ser ordenada na fila de follow-up.
    if (typeof value.dueAt !== "string" || !value.dueAt)
        fail("CrmTask", "missing dueAt");
    if (typeof value.ownerProfileId !== "string" || !value.ownerProfileId)
        fail("CrmTask", "missing ownerProfileId");
    if (value.status !== "open" && (typeof value.closedAt !== "string" || !value.closedAt)) {
        fail("CrmTask", "closed task requires closedAt");
    }
    return value;
}
export function assertCrmSegment(value) {
    if (!isRecord(value))
        fail("CrmSegment", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("CrmSegment", "missing id");
    if (typeof value.key !== "string" || !value.key)
        fail("CrmSegment", "missing key");
    if (typeof value.name !== "string" || !value.name)
        fail("CrmSegment", "missing name");
    if (typeof value.active !== "boolean")
        fail("CrmSegment", "missing active");
    // A regra em si NÃO é validada aqui: ela é `jsonb` e quem a interpreta é
    // `parseSegmentRule` (crm/segments.ts), que devolve "regra não reconhecida"
    // em vez de excluir o cliente do segmento em silêncio.
    if (value.rule === undefined || value.rule === null)
        fail("CrmSegment", "missing rule");
    return value;
}
